// The campaign firewall for the inventory manager.
//
// Gear must never cross from one campaign into another. On 2026-10-01 the DM
// opened the inventory screen with a party of five and saw twenty-five
// characters from every table the console had ever read, most with a "—"
// badge: the character LIST was unscoped while the snapshot RECORDS were
// scoped, so the screen offered destinations whose data it could not even see.
//
// Hiding the strangers is not enough, and that is the whole point of this
// module. The screen keeps its chosen target, its "adding to" target, its move
// destination and its export QUEUE in state that outlives a campaign switch,
// so a queue stamped in one campaign could still be exported into another
// after the switch. Every one of those paths asks a question here instead of
// trusting the picker it came from.
import { campaignSheets } from "./campaign";
import type { Character, Layout } from "./types";

export const DDB_PREFIX = "ddb-";
/** The shared party inventory: a real target, and always inside the open campaign. */
export const PARTY_KEY = "party";

/** The inventories this screen may touch: the open campaign's roster, narrowed to
 *  the sheets that actually have a D&D Beyond inventory to read or write. */
export function inventoryRoster(layout: Layout, campaign = layout.activeCampaign): Character[] {
  return campaignSheets(layout, campaign);
}

/** Is this target key one the open campaign owns? */
export function targetInCampaign(layout: Layout, key: string, campaign = layout.activeCampaign): boolean {
  if (!key) return false;
  if (key === PARTY_KEY) return true;
  return inventoryRoster(layout, campaign).some((c) => c.id === key);
}

/** A target the screen may safely show: the given one when the campaign owns it,
 *  else the party inventory. Used to heal state left over from another campaign. */
export function safeTarget(layout: Layout, key: string, campaign = layout.activeCampaign): string {
  return targetInCampaign(layout, key, campaign) ? key : PARTY_KEY;
}

/** Why a write must be refused, or "" when every end of it is inside the open
 *  campaign. The message names the campaign because the DM's first thought on
 *  seeing a refusal is "which table did this come from?". */
export function crossCampaignRefusal(layout: Layout, keys: Array<string | undefined>, campaign = layout.activeCampaign): string {
  const outside = keys.filter((k): k is string => Boolean(k)).filter((k) => !targetInCampaign(layout, k, campaign));
  if (!outside.length) return "";
  const names = outside.map((k) => {
    const found = layout.characters.find((c) => c.id === k);
    return found ? found.name : k;
  });
  const who = [...new Set(names)].join(", ");
  return `Refused: ${who} ${outside.length === 1 ? "is" : "are"} not in ${campaign || "this campaign"}. Gear cannot cross between campaigns.`;
}

/** A queued item remembers the campaign it was queued in, so a switch cannot
 *  carry it to another table's sheet. Entries queued before this existed have no
 *  stamp; they belong to whoever still has the character, which targetInCampaign
 *  decides. */
export interface ScopedQueueEntry { targetKey: string; campaignKey?: string; campaignName?: string }
export function queueInCampaign<T extends ScopedQueueEntry>(layout: Layout, queue: T[], campaignKey: string, campaign = layout.activeCampaign): T[] {
  return queue.filter((q) => {
    if (q.campaignKey || q.campaignName) return q.campaignKey === campaignKey || q.campaignName === campaign;
    return targetInCampaign(layout, q.targetKey, campaign);
  });
}

/* Above this, a stack is a PILE rather than a thing, and a drag of it is not a
   claim on all of it. 20 clears a bundle of 12 rations or 10 torches and catches
   a quiver of 60. */
export const BULK_THRESHOLD = 20;
const BULK_WORDS = ["ammunition", "arrow", "bolt", "bullet", "needle", "dart", "caltrop", "ball bearing"];
/* What a drag of a pile proposes instead. It must stay below BULK_THRESHOLD, or
   the proposal could exceed the pile it came from; the test asserts that rather
   than a Math.min here, which with these two numbers was unreachable. */
export const BULK_PART = 10;

/** How much of a stack a drag proposes to move.
 *
 *  Dragging a thing is a gesture about THAT THING, so the default is the whole
 *  stack: handing over the rope, the potion, the 12 rations is one motion and
 *  should be one click. Ammunition and other big piles are the exception - a
 *  quiver holds 60 arrows and nobody hands over 60 arrows, they hand over some.
 *  For those the proposal is a round number well under the pile, so the DM has
 *  to look at the field and type what they meant.
 *
 *  This only PRE-FILLS the move sheet; nothing reaches a character sheet until
 *  the DM confirms. */
export function dropQuantity(item: { quantity?: number; name?: string; itemType?: string }): number {
  const have = Math.max(1, Number(item.quantity) || 1);
  if (have <= BULK_THRESHOLD) return have;
  const hay = `${item.name || ""} ${item.itemType || ""}`.toLowerCase();
  return BULK_WORDS.some((w) => hay.includes(w)) ? BULK_PART : have;
}
